import { ReactNode, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, MoreVertical, Pencil, Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Modal } from '@/components/ui/modal';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { useI18n } from '@/hooks/useI18n';
import { IInstallment } from '@/lib/builder/models/payment-plan.model';
import { MarkInstallmentPaidModal } from '@/lib/builder/components/payment-plan/MarkInstallmentPaidModal';
import { installmentBadgeClassName } from '@/lib/builder/components/payment-plan/InstallmentStatusBadge';
import { formatPlanAmount, formatPlanDate } from '@/lib/builder/utils/payment-format';
import { MailLanguageSelector } from '@/lib/mail/MailLanguageSelector';
import { PersonCell } from '@/lib/payments/components/PersonCell';
import { installmentCaption } from '@/lib/payments/utils/installment-caption';
import {
    useClientMailLanguage,
    MAIL_DEFAULT_LANGUAGE,
    type MailLanguage,
} from '@/lib/shared-proposal/hooks/useClientMailLanguage';
import {
    DEFAULT_PAGE_SIZE,
    DUE_WINDOW_ALL,
    IDueInstallment,
    IPaymentsPage,
    PeopleDictionary,
    personOf,
} from '@/lib/payments/models/payments-dashboard.model';

interface DueInstallmentsCardProps {
    rows: IDueInstallment[];
    /** Click sul titolo del viaggio: apre la modale di dettaglio, non il builder. */
    onOpenItinerary: (itineraryId: string) => void;
    /** Anagrafiche citate dalle righe: le righe portano solo la chiave. */
    people: PeopleDictionary;
    dueWindowDays: number;
    /** La pagina sta arrivando: righe scheletro al posto delle righe, paginatore fermo. */
    isLoading: boolean;
    /** Cambia a ogni risposta: le righe rientrano animate anche con gli stessi id. */
    animationKey: string | number;
    /** TD, agenzia e admin gestiscono le rate (modifica piano, solleciti); il viaggiatore paga. */
    canManage: boolean;
    /**
     * Chi puo' registrare un incasso a mano: dal 02/09/2026 tutti quelli che passano
     * `can_manage_itinerary()`, non piu' il solo admin. Resta una scrittura contabile che
     * nessuno verifica contro Stripe, ma non e' piu' gratuita — apre una commissione a
     * debito verso Place2Be, con il nome di chi l'ha dichiarata sopra e una notifica agli
     * admin. Il guard vero sta in `mark_installment_paid`; qui si nasconde solo cio' che
     * verrebbe rifiutato.
     */
    canMarkPaid: boolean;
    canPay: boolean;
    /**
     * Aprono la scheda di dettaglio nel pannello a destra, come dalla Panoramica e dal
     * dettaglio di una richiesta. Assenti (il viaggiatore) i pallini restano quello che
     * erano: due facce, non due porte verso numeri che non deve vedere.
     */
    onOpenClient?: (clientKey: string) => void;
    onOpenAdvisor?: (advisorId: string) => void;
    isMarkingPaid: boolean;
    /**
     * Rata per cui si sta aprendo il checkout, se ce n'e' una. Serve l'id e non un
     * booleano: il pulsante in attesa deve essere quello cliccato, non tutti quelli a
     * schermo. Finche' e' valorizzato gli altri "Paga" restano disabilitati, perche' due
     * checkout aperti sulla stessa rata si contenderebbero la sessione Stripe.
     */
    payingInstallmentId: string | null;
    isSendingReminder: boolean;
    /** Rate gia' sollecitate in questa sessione: il database rifiuta comunque un secondo
     *  invio entro un'ora, ma il pulsante deve dirlo prima del click. */
    remindedIds: Set<string>;
    onSendReminder: (params: { installmentId: string; language: MailLanguage }) => void;
    onMarkPaid: (params: {
        installmentId: string;
        itineraryId: string;
        paymentMethod: string;
        notifyClient: boolean;
        language: MailLanguage;
    }) => void;
    onPay: (params: { installmentId: string; itineraryId: string }) => void;
    /**
     * Apre l'editor del piano a cui la rata appartiene, con tutte le altre rate, e con
     * questa evidenziata e a fuoco.
     */
    onEditPlan: (itineraryId: string, installmentId: string) => void;
    /** Nascosta per TD e viaggiatore: sarebbe una colonna con sempre lo stesso nome. */
    showDesigner: boolean;
    /**
     * Nasconde il nome cliente sotto il titolo del viaggio: per il viaggiatore sarebbe
     * sempre il proprio, su ogni riga. Il lessico del denaro non passa piu' di qui, lo
     * sceglie useMoneyCopy() dal ruolo.
     */
    travelerView?: boolean;
    /**
     * Impaginazione della RPC: la card non sa quante pagine esistono. `page` assente
     * finche' la prima risposta non arriva (paginatore a scheletro).
     */
    page?: IPaymentsPage | null;
    onPageChange?: (page: number) => void;
    /**
     * Falso per la scheda "Da confermare": la finestra non vale per le proposte, la RPC
     * le restituisce tutte, a qualunque data scadano.
     */
    showWindowHint?: boolean;
    /** Sopra la tabella, a tutta larghezza: spiegazione e totali della vista. */
    summary?: ReactNode;
    emptyMessage?: string;
}

/**
 * Adatta una riga della dashboard alla forma che la modale di conferma si aspetta.
 *
 * La modale nasce nella card del builder e legge solo label, scadenza e importo; il resto
 * di IInstallment non le serve. Riusarla e' obbligatorio: e' l'unico punto in cui l'utente
 * viene avvisato che il primo incasso manda l'itinerario a completato, che fa scadere le
 * proposte alternative e che la commissione Place2Be resta a suo debito.
 */
const toModalInstallment = (row: IDueInstallment): IInstallment =>
    ({
        id: row.installment_id,
        label: row.label ?? '',
        amount: row.amount,
        due_date: row.due_date,
        seq: row.seq,
        status: 'pending',
        is_overdue: row.is_overdue,
        payment_processing: row.payment_processing,
    }) as IInstallment;

/**
 * Rate da incassare, scadute in cima.
 *
 * Scadute e in scadenza stanno nella stessa tabella di proposito: sono la stessa coda di
 * lavoro, e in due liste separate si finirebbe per guardarne una sola. La distinzione la
 * porta il badge, non la posizione nella pagina.
 */
export const DueInstallmentsCard = ({
    rows,
    onOpenItinerary,
    people,
    dueWindowDays,
    isLoading,
    animationKey,
    canManage,
    canMarkPaid,
    canPay,
    onOpenClient,
    onOpenAdvisor,
    isMarkingPaid,
    payingInstallmentId,
    isSendingReminder,
    remindedIds,
    onSendReminder,
    onMarkPaid,
    onPay,
    onEditPlan,
    showDesigner,
    travelerView,
    page,
    onPageChange,
    showWindowHint = true,
    summary,
    emptyMessage,
}: DueInstallmentsCardProps) => {
    const { t } = useI18n();
    const [rowToConfirm, setRowToConfirm] = useState<IDueInstallment | null>(null);
    /**
     * Rata per cui si sta chiedendo conferma del sollecito. Il click sulla voce di menu
     * non invia piu' nulla: l'email parte solo dal pulsante della modale, perche' un
     * sollecito partito per sbaglio arriva al cliente e non si annulla.
     */
    const [rowToRemind, setRowToRemind] = useState<IDueInstallment | null>(null);

    /**
     * Lingua delle mail della riga aperta — sollecito o incasso: `client_key` e' l'email
     * normalizzata del cliente, e la lingua si chiede per quella sola riga, non per tutta
     * la tabella.
     */
    const selectedRow = rowToRemind ?? rowToConfirm;
    const selectedClientEmail =
        personOf(people, selectedRow?.client_key)?.email ?? selectedRow?.client_key ?? null;
    const {
        profileLanguage: clientProfileLanguage,
        isLoading: isCheckingClientLanguage,
        lookupFailed: clientLanguageLookupFailed,
    } = useClientMailLanguage(selectedClientEmail);
    const [mailLanguage, setMailLanguage] = useState<MailLanguage | null>(null);

    // Ogni riga riparte dalla lingua del suo cliente: una scelta fatta sulla riga prima
    // non deve restare appiccicata a quella dopo.
    useEffect(() => {
        setMailLanguage(null);
    }, [selectedClientEmail]);

    useEffect(() => {
        if (mailLanguage === null && clientProfileLanguage) setMailLanguage(clientProfileLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientProfileLanguage]);

    const effectiveMailLanguage = mailLanguage ?? clientProfileLanguage ?? MAIL_DEFAULT_LANGUAGE;

    /**
     * Il pulsante "Paga" sta su una rata sola per viaggio: la prima aperta. Le scadenze
     * di un piano sono crescenti per validazione, quindi la prima per data e' anche la
     * prima per numero. Due CTA di pagamento sullo stesso viaggio si contenderebbero il
     * click e Stripe accetterebbe comunque solo la rata che la edge function rilegge.
     */
    const payableIds = useMemo(() => {
        const seen = new Set<string>();
        const payable = new Set<string>();
        rows.forEach((row) => {
            if (seen.has(row.itinerary_id)) return;
            seen.add(row.itinerary_id);
            payable.add(row.installment_id);
        });
        return payable;
    }, [rows]);

    const dueLabel = (row: IDueInstallment) => {
        if (row.payment_processing) return t('payments.due.processing');
        if (row.is_overdue) {
            return t('payments.due.overdueBy', { days: Math.abs(row.days_to_due) });
        }
        if (row.days_to_due === 0) return t('payments.due.today');
        return t('payments.due.inDays', { days: row.days_to_due });
    };

    const columns: DataTableColumn<IDueInstallment>[] = [
        {
            id: 'trip',
            header: t('payments.columns.trip'),
            cellClassName: 'align-top',
            cell: (row) => (
                <>
                    <button
                        type="button"
                        className="font-medium text-gray-900 hover:underline text-left"
                        onClick={() => onOpenItinerary(row.itinerary_id)}
                    >
                        {row.itinerary_title}
                    </button>

                    {/* Di quale rata si tratta, sotto il viaggio a cui appartiene: sono due
                        livelli della stessa cosa, e letti insieme dicono "Kenya, la seconda
                        di cinque".

                        Apre il piano INTERO, non la singola rata: gli importi devono
                        quadrare col totale, e una riga modificata da sola sfonderebbe la
                        quadratura senza mostrare dove. */}
                    {canManage ? (
                        <button
                            type="button"
                            className="block text-xs text-gray-500 hover:text-gray-900 hover:underline text-left"
                            title={t('payments.actions.editPlan')}
                            onClick={() => onEditPlan(row.itinerary_id, row.installment_id)}
                        >
                            {installmentCaption(t, row)}
                        </button>
                    ) : (
                        <span className="block text-xs text-gray-500">{installmentCaption(t, row)}</span>
                    )}
                </>
            ),
        },
        /* La colonna e' del cliente: e' di lui che si parla quando si guarda una
           scadenza, ed e' su di lui che si cerca lungo la lista. Per il viaggiatore
           sarebbe il proprio nome su ogni riga. */
        ...(!travelerView
            ? [
                  {
                      id: 'client',
                      header: t('payments.columns.client'),
                      headClassName: 'whitespace-nowrap',
                      cellClassName: 'align-top',
                      cell: (row: IDueInstallment) => (
                          <PersonCell
                              person={personOf(people, row.client_key)}
                              hint={t('payments.columns.client')}
                              withEmail
                              onOpen={
                                  row.client_key && onOpenClient
                                      ? () => onOpenClient(row.client_key as string)
                                      : undefined
                              }
                          />
                      ),
                  },
              ]
            : []),
        /* Solo admin e agenzia: un advisor vede le proprie rate e basta, quindi la
           colonna ripeterebbe il suo nome su ogni riga. La condizione e' la stessa del
           filtro per collaboratore. */
        ...(showDesigner
            ? [
                  {
                      id: 'designer',
                      header: t('payments.columns.designer'),
                      headClassName: 'whitespace-nowrap',
                      cellClassName: 'align-top',
                      cell: (row: IDueInstallment) => (
                          <PersonCell
                              person={personOf(people, row.designer_id)}
                              hint={t('payments.columns.designer')}
                              withEmail
                              onOpen={
                                  row.designer_id && onOpenAdvisor
                                      ? () => onOpenAdvisor(row.designer_id as string)
                                      : undefined
                              }
                          />
                      ),
                  },
              ]
            : []),
        {
            id: 'dueDate',
            header: t('payments.columns.dueDate'),
            headClassName: 'w-px whitespace-nowrap',
            cellClassName: 'align-top whitespace-nowrap',
            cell: (row) => (
                <>
                    <span className="text-gray-900">{formatPlanDate(row.due_date)}</span>
                    <span className="block mt-0.5">
                        {/* Colori dal badge delle rate: qui cambia solo il testo ("Scaduta
                            da 3 gg" invece di "Scaduta"), non il significato dello stato. */}
                        <Badge
                            className={installmentBadgeClassName({
                                status: 'pending',
                                isOverdue: row.is_overdue,
                                isProcessing: row.payment_processing,
                            })}
                        >
                            {dueLabel(row)}
                        </Badge>
                    </span>
                </>
            ),
        },
        {
            id: 'amount',
            header: t('payments.columns.amount'),
            headClassName: 'w-px whitespace-nowrap text-right',
            cellClassName: 'align-top text-right font-semibold text-gray-900 tabular-nums whitespace-nowrap',
            cell: (row) => formatPlanAmount(row.amount),
        },
        {
            id: 'actions',
            header: null,
            headClassName: 'w-px',
            cellClassName: 'align-top text-right whitespace-nowrap',
            cell: (row) => (
                <div className="flex items-center justify-end gap-1">
                    {/* Tre azioni di gestione in un menu: da bottoni affiancati occupavano
                        piu' larghezza dei dati della riga, e sono tutte azioni occasionali. */}
                    {canManage && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    aria-label={t('common.actions')}
                                >
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {/* Modifica importo, scadenza o promemoria della rata. Si
                                    apre comunque il piano intero, con questa riga a fuoco:
                                    gli importi devono quadrare col totale. */}
                                <DropdownMenuItem onClick={() => onEditPlan(row.itinerary_id, row.installment_id)}>
                                    <Pencil className="mr-2 h-4 w-4" />
                                    {t('payments.actions.editPlan')}
                                </DropdownMenuItem>
                                {/* Sollecito al cliente. Sulle rate scadute e' l'unico invio
                                    possibile: i promemoria pianificati hanno tutti offset
                                    negativi e dopo la scadenza non parte piu' nulla da solo. */}
                                <DropdownMenuItem
                                    disabled={
                                        isSendingReminder ||
                                        remindedIds.has(row.installment_id) ||
                                        row.payment_processing
                                    }
                                    title={
                                        row.payment_processing
                                            ? t('payments.actions.processingHint')
                                            : row.is_overdue
                                            ? t('payments.actions.remindOverdue')
                                            : t('payments.actions.remind')
                                    }
                                    onClick={() => setRowToRemind(row)}
                                >
                                    <Send className="mr-2 h-4 w-4" />
                                    {remindedIds.has(row.installment_id)
                                        ? t('payments.actions.reminded')
                                        : t('payments.actions.remind')}
                                </DropdownMenuItem>
                                {canMarkPaid && (
                                    <DropdownMenuItem
                                        // Registrarla a mano mentre l'addebito e' in volo
                                        // significa incassare due volte.
                                        disabled={isMarkingPaid || row.payment_processing}
                                        title={
                                            row.payment_processing
                                                ? t('payments.actions.processingHint')
                                                : undefined
                                        }
                                        onClick={() => setRowToConfirm(row)}
                                    >
                                        <CheckCircle2 className="mr-2 h-4 w-4" />
                                        {t('payments.actions.markPaid')}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    {/* Il tentativo aperto non toglie il pulsante: chi ha aperto il checkout
                        e non ha concluso deve poter ripartire. */}
                    {canPay && payableIds.has(row.installment_id) && (
                        <Button
                            type="button"
                            size="sm"
                            disabled={payingInstallmentId !== null}
                            aria-busy={payingInstallmentId === row.installment_id}
                            onClick={() =>
                                onPay({
                                    installmentId: row.installment_id,
                                    itineraryId: row.itinerary_id,
                                })
                            }
                        >
                            {payingInstallmentId === row.installment_id ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('payments.actions.payLoading')}
                                </>
                            ) : (
                                t('payments.actions.pay')
                            )}
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            {/* Altezza dal contenitore, non dal contenuto: la tabella riempie la scheda e
                scorre dentro di se'. Testata e paginazione restano ferme ai due capi --
                sono i due punti da cui si capisce cosa si sta guardando e dove si e'
                arrivati. Il titolo non c'e': e' gia' quello della scheda aperta. */}
            <div className="h-full flex flex-col min-h-0 gap-3">
                {(showWindowHint || summary) && (
                    <div className="flex-shrink-0 space-y-3">
                        {showWindowHint && (
                            <p className="text-xs text-gray-500">
                                {dueWindowDays === DUE_WINDOW_ALL
                                    ? t('payments.due.windowHintAll')
                                    : t('payments.due.windowHint', { days: dueWindowDays })}
                            </p>
                        )}
                        {summary}
                    </div>
                )}

                <div className="flex-1 min-h-0">
                    <DataTable
                        data={rows}
                        columns={columns}
                        getRowId={(row) => row.installment_id}
                        getRowClassName={(row) => (row.is_overdue ? 'bg-red-50/60 hover:bg-red-50' : undefined)}
                        pageSize={page?.page_size ?? DEFAULT_PAGE_SIZE}
                        serverPagination={
                            onPageChange
                                ? {
                                      page: page?.page ?? 1,
                                      totalItems: page?.total_count ?? 0,
                                      onPageChange,
                                      disabled: isLoading,
                                  }
                                : undefined
                        }
                        paginated={false}
                        isLoading={isLoading}
                        animationKey={animationKey}
                        emptyState={emptyMessage ?? t('payments.due.empty')}
                        tableClassName="table-auto min-w-[760px]"
                    />
                </div>
            </div>

            {/* Conferma del sollecito: la modale del progetto, non un AlertDialog, come
                per l'incasso manuale qui accanto. */}
            <Modal
                isOpen={Boolean(rowToRemind)}
                onClose={() => setRowToRemind(null)}
                size="sm"
                title={t('payments.actions.remindConfirmTitle')}
                footer={
                    <>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setRowToRemind(null)}
                            disabled={isSendingReminder}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="button"
                            disabled={isSendingReminder}
                            onClick={() => {
                                if (!rowToRemind) return;
                                onSendReminder({
                                    installmentId: rowToRemind.installment_id,
                                    language: effectiveMailLanguage,
                                });
                                setRowToRemind(null);
                            }}
                        >
                            {t('payments.actions.remindConfirmCta')}
                        </Button>
                    </>
                }
            >
                <div className="flex flex-col gap-4 px-6 py-4">
                    <p className="text-sm text-gray-700">
                        {t('payments.actions.remindConfirmBody')}
                    </p>
                    {rowToRemind && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1">
                            <span className="font-medium text-gray-900">
                                {rowToRemind.itinerary_title}
                            </span>
                            <span className="text-sm text-gray-500">
                                {t('payments.actions.remindConfirmDetail', {
                                    label: rowToRemind.label ?? '—',
                                    amount: formatPlanAmount(rowToRemind.amount),
                                    date: formatPlanDate(rowToRemind.due_date),
                                })}
                            </span>
                        </div>
                    )}
                    <MailLanguageSelector
                        id="reminder-mail-language"
                        value={effectiveMailLanguage}
                        onChange={setMailLanguage}
                        profileLanguage={clientProfileLanguage}
                        isLoading={isCheckingClientLanguage}
                        lookupFailed={clientLanguageLookupFailed}
                        disabled={isSendingReminder}
                    />
                </div>
            </Modal>

            {/* Montata solo per l'admin: la voce di menu che la apre non esiste per gli
                altri ruoli, e la RPC li rifiuterebbe comunque. */}
            {canMarkPaid && (
            <MarkInstallmentPaidModal
                installment={rowToConfirm ? toModalInstallment(rowToConfirm) : null}
                itineraryId={rowToConfirm?.itinerary_id}
                isOpen={Boolean(rowToConfirm)}
                isSaving={isMarkingPaid}
                formatAmount={formatPlanAmount}
                formatDate={formatPlanDate}
                onClose={() => setRowToConfirm(null)}
                clientProfileLanguage={clientProfileLanguage}
                isCheckingClientLanguage={isCheckingClientLanguage}
                clientLanguageLookupFailed={clientLanguageLookupFailed}
                onConfirm={({ installmentId, paymentMethod, notifyClient, language }) => {
                    if (!rowToConfirm) return;
                    onMarkPaid({
                        installmentId,
                        itineraryId: rowToConfirm.itinerary_id,
                        paymentMethod,
                        notifyClient,
                        language,
                    });
                    setRowToConfirm(null);
                }}
            />
            )}
        </>
    );
};
